/**
 * Canonical lesson voice-moments map (launch path, 10 lessons).
 *
 * Automatic instructor Voice Moments that fire INSIDE regular lessons at
 * approved triggers (post-video bridge and/or the first decision check).
 * Each launch lesson has exactly two approved moments.
 *
 * These are separate from, and must not be confused with:
 *  - `lumiHolocardVoiceMap.ts` — learner-clicked, optional Lumi support.
 *  - `instructorVoiceMap.ts`   — automatic MODULE voice moments.
 *  - lesson video narration + closed captions (lesson content).
 *
 * The Supabase edge function `instructor-voice` mirrors these transcripts
 * server-side as a trusted allowlist. Transcripts must stay BYTE-IDENTICAL
 * with that mirror so generate-once/cache keys match and no duplicate
 * ElevenLabs request is made for the same approved static line.
 */

import type { InstructorId } from "./instructorVoiceMap";

export type LessonVoiceStage = "post_video" | "pre_decision" | "post_decision";

export interface LessonVoiceMoment {
  /** Stable canonical lesson_id from the launch roster. */
  lessonId: string;
  /** Stable, public-facing slug (camelCase topic). */
  lessonSlug: string;
  stage: LessonVoiceStage;
  /** `lesson.<slug>.<stage>` — server allowlist key. */
  momentId: string;
  instructor: InstructorId;
  transcript: string;
  estimatedDurationSeconds: number;
}

function moment(
  lessonId: string,
  lessonSlug: string,
  stage: LessonVoiceStage,
  instructor: InstructorId,
  transcript: string,
  estimatedDurationSeconds: number,
): LessonVoiceMoment {
  const stageKey =
    stage === "post_video" ? "postVideo" : stage === "pre_decision" ? "preDecision" : "postDecision";
  return {
    lessonId,
    lessonSlug,
    stage,
    momentId: `lesson.${lessonSlug}.${stageKey}`,
    instructor,
    transcript,
    estimatedDurationSeconds,
  };
}

const NEEDS_WANTS = "709b0a91-2103-4ddf-9a70-e4ee5dae0b41";
const OPPORTUNITY_COST = "f1ec70b9-2619-41b4-b2e8-f89425bf4e1b";
const FINANCIAL_TRADEOFFS = "9979b6d9-b308-49f4-b9a5-29e5a6a7ff3f";
const DELAYED_GRATIFICATION = "e29e634d-e035-42fb-8568-a5241a02d595";
const CASH_FLOW = "d2b87cb1-7774-4029-87e0-be51ab14840e";
const CREDIT_SCORE_BASICS = "c6d62ce8-0b9e-40ff-b09d-64bcda1f6210";
const BORROWING_MONEY = "db6fdc3b-f16d-4fe5-a750-c65c2c8dc5a1";
const MINIMUM_PAYMENTS = "fcbdef95-7194-4921-8cb7-dfdfebda0ee0";
const URGENCY_RED_FLAG = "e1e29e49-0074-48f5-ba15-70374284867e";
const TOO_GOOD_TO_BE_TRUE = "846aa833-7dd2-4059-892a-6fbebf6a2c2e";

export const LESSON_VOICE_MOMENTS: LessonVoiceMoment[] = [
  moment(NEEDS_WANTS, "needsWants", "pre_decision", "aelyra",
    "Before you choose — ask which version of you is making this call: today's, or next month's.", 8),
  moment(NEEDS_WANTS, "needsWants", "post_decision", "aelyra",
    "Strong call. Every want you delay is a need you can fund later without flinching.", 7),

  moment(OPPORTUNITY_COST, "opportunityCost", "pre_decision", "caelion",
    "What you pick is loud. What you give up is silent. Listen for the silence.", 7),
  moment(OPPORTUNITY_COST, "opportunityCost", "post_decision", "caelion",
    "Good. You priced what you did not choose, not just what you did.", 6),

  moment(FINANCIAL_TRADEOFFS, "financialTradeoffs", "pre_decision", "caelion",
    "There is no clean answer here. There is a wiser one. Find which value you are protecting.", 8),
  moment(FINANCIAL_TRADEOFFS, "financialTradeoffs", "post_decision", "caelion",
    "Notice what you ranked first. That ranking is your strategy, whether you named it or not.", 8),

  moment(DELAYED_GRATIFICATION, "delayedGratification", "pre_decision", "aelyra",
    "Pause before you pick. Future-you is in the room, and they get a vote.", 7),
  moment(DELAYED_GRATIFICATION, "delayedGratification", "post_decision", "aelyra",
    "Waiting is not losing. It is turning patience into options.", 6),

  moment(CASH_FLOW, "cashFlow", "post_video", "aelyra",
    "A balance is a photo. Cash flow is the movie. Watch the movie.", 6),
  moment(CASH_FLOW, "cashFlow", "pre_decision", "aelyra",
    "Do not ask, can I afford it today? Ask, can I afford it every month it stays?", 8),

  moment(CREDIT_SCORE_BASICS, "creditScoreBasics", "post_video", "aelyra",
    "Your score is not a grade. It is a signal built from repeated financial behavior.", 7),
  moment(CREDIT_SCORE_BASICS, "creditScoreBasics", "pre_decision", "aelyra",
    "There are no shortcuts here — only patterns. Pick the pattern you want repeated about you.", 8),

  moment(BORROWING_MONEY, "borrowingMoney", "pre_decision", "aelyra",
    "Borrowed money is a promise wearing a price tag. Read both before you sign.", 7),
  moment(BORROWING_MONEY, "borrowingMoney", "post_decision", "aelyra",
    "Good. You treated the loan like a future responsibility, not free money for today.", 8),

  moment(MINIMUM_PAYMENTS, "minimumPayments", "post_video", "aelyra",
    "Minimums feel kind. They are not. They are patient.", 5),
  moment(MINIMUM_PAYMENTS, "minimumPayments", "pre_decision", "aelyra",
    "Do not ask what fits this month. Ask what it costs to keep paying it.", 7),

  moment(URGENCY_RED_FLAG, "urgencyRedFlag", "post_video", "caelion",
    "Speed is the oldest trick. Anyone rushing you is trying to choose for you.", 7),
  moment(URGENCY_RED_FLAG, "urgencyRedFlag", "pre_decision", "caelion",
    "Before you answer — would this offer survive a 24-hour delay? If not, it is not an offer. It is pressure.", 9),

  moment(TOO_GOOD_TO_BE_TRUE, "tooGoodToBeTrue", "pre_decision", "caelion",
    "If the upside is loud, look for the downside it is drowning out.", 6),
  moment(TOO_GOOD_TO_BE_TRUE, "tooGoodToBeTrue", "post_decision", "caelion",
    "Good instinct. Real opportunities can survive a hard question. Scams cannot.", 7),
];

export const LESSON_VOICE_MAP: Record<string, LessonVoiceMoment> = Object.fromEntries(
  LESSON_VOICE_MOMENTS.map((m) => [m.momentId, m]),
);

const LESSON_INDEX: Record<string, Partial<Record<LessonVoiceStage, LessonVoiceMoment>>> = {};
for (const m of LESSON_VOICE_MOMENTS) {
  (LESSON_INDEX[m.lessonId] ||= {})[m.stage] = m;
}

export function getLessonMoment(momentId: string): LessonVoiceMoment | undefined {
  return LESSON_VOICE_MAP[momentId];
}

/** Look up the moment for a (lessonId, stage) pair, or undefined when none. */
export function getLessonMomentFor(
  lessonId: string | undefined | null,
  stage: LessonVoiceStage,
): LessonVoiceMoment | undefined {
  if (!lessonId) return undefined;
  return LESSON_INDEX[lessonId]?.[stage];
}

/** Convenience: all moment IDs for a lesson — used for preload. */
export function getLessonMomentIds(lessonId: string | undefined | null): string[] {
  if (!lessonId) return [];
  const bucket = LESSON_INDEX[lessonId];
  if (!bucket) return [];
  return Object.values(bucket).map((m) => m!.momentId);
}
